import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { playKitchenAlertSound } from '../../shared/audio/kitchenAlertSound';
import { getCounterReadySoundEnabled } from '../../shared/localPrefs';
import { captureSentryError } from '../../shared/sentry/sentryUser';
import { showSuccess } from '../../shared/ui/appToast';
import { fetchHospitalitySnapshot, type HospitalitySnapshot } from '../infra/hospitalityRepo';
import { useMesasLiveRefresh } from '../hooks/useMesasLiveRefresh';
import type { TableDraftSummary } from '../domain/hospitalityTypes';

type SignalFingerprint = {
  openSignalCount: number;
  hasCallWaiter: boolean;
  hasReadyToOrder: boolean;
  hasRequestBill: boolean;
};

function fingerprint(g: TableDraftSummary): SignalFingerprint {
  return {
    openSignalCount: g.openSignalCount,
    hasCallWaiter: g.hasCallWaiter,
    hasReadyToOrder: g.hasReadyToOrder,
    hasRequestBill: g.hasRequestBill,
  };
}

function kindLabel(prev: SignalFingerprint | undefined, next: SignalFingerprint): string | null {
  if (next.hasCallWaiter && !prev?.hasCallWaiter) return 'Te llaman';
  if (next.hasReadyToOrder && !prev?.hasReadyToOrder) return 'Ya pidieron';
  if (next.hasRequestBill && !prev?.hasRequestBill) return 'Piden la cuenta';
  if (next.openSignalCount > (prev?.openSignalCount ?? 0)) return 'Señal de mesa';
  return null;
}

/**
 * Toast + beep when a new open guest_signal appears (Mesas / POS).
 * Uses snapshot table_drafts flags; reuses kitchen sound pref.
 */
export function GuestSignalAlertProvider({ children }: { children: React.ReactNode }) {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const hasTables = useHasBusinessCapability('tables');
  const canViewTables = useModulePermission('Tables', 'view');
  const canOperate = useModulePermission('Tables', 'operate');
  const shouldNotify = hasTables && (canViewTables || canOperate);

  const prevByTableRef = useRef<Map<string, SignalFingerprint>>(new Map());
  const primedRef = useRef(false);
  const scopeRef = useRef<{ branchId: string | null; enabled: boolean } | null>(null);
  const requestRef = useRef(0);

  useLayoutEffect(() => {
    const scope = { branchId: branchId ?? null, enabled: shouldNotify };
    scopeRef.current = scope;
    primedRef.current = false;
    prevByTableRef.current = new Map();
    return () => {
      if (scopeRef.current === scope) scopeRef.current = null;
    };
  }, [branchId, shouldNotify]);

  const syncSignals = useCallback(async () => {
    const scope = scopeRef.current;
    if (!branchId || !shouldNotify || !scope?.enabled || scope.branchId !== branchId) return;
    const request = ++requestRef.current;
    let snap: HospitalitySnapshot;
    try {
      snap = await fetchHospitalitySnapshot(branchId, 30);
    } catch (error) {
      if (scopeRef.current === scope && requestRef.current === request) {
        captureSentryError(error, { tags: { module: 'hospitality', op: 'guest_signals_snapshot' } });
      }
      return;
    }
    if (scopeRef.current !== scope || requestRef.current !== request) return;
    const nextMap = new Map<string, SignalFingerprint>();

    for (const draft of snap.tableDrafts) {
      const fp = fingerprint(draft);
      nextMap.set(draft.diningTableId, fp);

      if (!primedRef.current) continue;

      const prev = prevByTableRef.current.get(draft.diningTableId);
      const label = kindLabel(prev, fp);
      if (!label) continue;

      const tableName = snap.diningTables.find((t) => t.id === draft.diningTableId)?.name?.trim();
      const mesaLabel = tableName
        ? /^mesa\b/i.test(tableName)
          ? tableName
          : `Mesa ${tableName}`
        : 'Mesa';
      showSuccess(label, mesaLabel);
      void getCounterReadySoundEnabled().then((v) => {
        if (scopeRef.current === scope && v !== '0') playKitchenAlertSound();
      }).catch((error) => {
        if (scopeRef.current === scope) {
          captureSentryError(error, { tags: { module: 'hospitality', op: 'guest_signal_sound' } });
        }
      });
    }

    prevByTableRef.current = nextMap;
    primedRef.current = true;
  }, [branchId, shouldNotify]);

  useMesasLiveRefresh(branchId, () => void syncSignals(), {
    enabled: shouldNotify,
  });

  useEffect(() => {
    void syncSignals();
  }, [syncSignals]);

  return <>{children}</>;
}
